import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Navigation } from './Navigation';
import {
  fireCalculatorService,
  Strategy,
  FireResult,
  FireInput,
  CustomETF,
} from '../services/fireCalculatorService';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import {
  LineChart, Line, XAxis, YAxis, Tooltip, ReferenceLine,
  ResponsiveContainer, Legend,
} from 'recharts';
import './Dashboard.css';
import './FireCalculator.css';

type TabType = 'input' | 'results' | 'projections';

const RISK_COLORS: Record<number, string> = {
  0: '#6b7280', // custom
  1: '#22c55e', // conservative
  2: '#3b82f6', // balanced / dividend
  3: '#f59e0b', // growth
  4: '#ef4444', // tech aggressive
};

const STRATEGY_COMPARE_COLORS = ['#3b82f6', '#22c55e', '#f59e0b', '#ef4444'];

const MAX_STRATEGIES = 4;

const DEFAULT_CUSTOM_ETFS: CustomETF[] = [
  { symbol: '', allocation: 0.34, base_return: 0.08, best_return: 0.15, worst_return: -0.03 },
  { symbol: '', allocation: 0.33, base_return: 0.08, best_return: 0.15, worst_return: -0.03 },
  { symbol: '', allocation: 0.33, base_return: 0.08, best_return: 0.15, worst_return: -0.03 },
];

export const FireCalculator: React.FC = () => {
  const { token, user } = useAuth();
  const isPremium = user?.effectiveTier === 'premier';

  // Strategy state
  const [strategies, setStrategies] = useState<Strategy[]>([]);
  const [selectedStrategyIds, setSelectedStrategyIds] = useState<string[]>(['balanced']);
  const [customETFs, setCustomETFs] = useState<CustomETF[]>(DEFAULT_CUSTOM_ETFS);
  const [customAllocations, setCustomAllocations] = useState<number[] | null>(null);

  // Input state
  const [fireTarget, setFireTarget] = useState<number>(2000000);
  const [currentSavings, setCurrentSavings] = useState<number>(50000);
  const [annualContribution, setAnnualContribution] = useState<number>(60000);
  const [frequency, setFrequency] = useState<'monthly' | 'quarterly' | 'annual'>('monthly');

  // UI state
  const [activeTab, setActiveTab] = useState<TabType>('input');
  const [loading, setLoading] = useState(false);
  const [strategiesLoading, setStrategiesLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showGuide, setShowGuide] = useState(false);
  const [maxLimitHint, setMaxLimitHint] = useState(false);
  const [loadingProgress, setLoadingProgress] = useState('');

  // Result state
  const [results, setResults] = useState<Record<string, FireResult>>({});

  // Multi-mode UI state
  const [expandedAccordions, setExpandedAccordions] = useState<Set<string>>(new Set());
  const [projSubTab, setProjSubTab] = useState<string>('');

  // Derived
  const isCustomSelected = selectedStrategyIds.includes('custom');
  const isMultiMode = Object.keys(results).length > 1;
  const hasResults = Object.keys(results).length > 0;
  const singleResult = !isMultiMode && hasResults ? Object.values(results)[0] : null;

  // Fetch strategies on mount
  useEffect(() => {
    if (!token) return;
    setStrategiesLoading(true);
    fireCalculatorService.getStrategies(token)
      .then(res => setStrategies(res.strategies))
      .catch(() => {/* strategies will be empty; user can still use custom */})
      .finally(() => setStrategiesLoading(false));
  }, [token]);

  // Toggle strategy selection
  const selectStrategy = (id: string) => {
    setSelectedStrategyIds(prev => {
      // Custom can only be selected alone
      if (id === 'custom') {
        return prev.includes('custom') ? [] : ['custom'];
      }

      // If custom is currently selected, deselect it and select the new one
      if (prev.includes('custom')) {
        return [id];
      }

      // Toggle the strategy
      if (prev.includes(id)) {
        return prev.filter(s => s !== id);
      }

      // Check max limit
      if (prev.length >= MAX_STRATEGIES) {
        setMaxLimitHint(true);
        setTimeout(() => setMaxLimitHint(false), 2000);
        return prev;
      }

      return [...prev, id];
    });
    setCustomAllocations(null);
  };

  const selectedStrategy = selectedStrategyIds.length === 1
    ? strategies.find(s => s.id === selectedStrategyIds[0])
    : null;

  // Handlers
  const handleCustomETFChange = (idx: number, field: keyof CustomETF, value: string | number) => {
    setCustomETFs(prev => {
      const updated = [...prev];
      updated[idx] = { ...updated[idx], [field]: value };
      return updated;
    });
  };

  const handleAllocationOverride = (idx: number, value: number) => {
    const current = customAllocations || (selectedStrategy?.etfs.map(e => e.allocation) ?? []);
    const updated = [...current];
    updated[idx] = value;
    setCustomAllocations(updated);
  };

  const runCalculation = async () => {
    if (!token) {
      setError('Authentication required');
      return;
    }
    if (selectedStrategyIds.length === 0) {
      setError('Select at least one strategy');
      return;
    }

    setLoading(true);
    setError(null);
    setLoadingProgress('');

    const ids = selectedStrategyIds;
    const newResults: Record<string, FireResult> = {};
    let completed = 0;

    try {
      const promises = ids.map(async (strategyId) => {
        const input: FireInput = {
          fire_target: fireTarget,
          current_savings: currentSavings,
          annual_contribution: annualContribution,
          strategy_id: strategyId,
          contribution_frequency: frequency,
          custom_allocations: strategyId !== 'custom' && ids.length === 1 ? customAllocations : null,
          custom_etfs: strategyId === 'custom' ? customETFs.map(e => ({
            ...e,
            symbol: e.symbol.toUpperCase(),
          })) : null,
        };

        const calcResult = await fireCalculatorService.calculate(token, input);
        newResults[strategyId] = calcResult;
        completed++;
        if (ids.length > 1) {
          setLoadingProgress(`Calculating ${completed}/${ids.length}...`);
        }
        return calcResult;
      });

      await Promise.all(promises);
      setResults(newResults);
      setActiveTab('results');

      // Set default projections sub-tab
      if (ids.length > 1) {
        setProjSubTab(ids[0]);
      }
    } catch (err: any) {
      const msg = err.response?.data?.message || 'Calculation failed. Check your inputs and try again.';
      setError(msg);
    } finally {
      setLoading(false);
      setLoadingProgress('');
    }
  };

  // Formatters
  const fmt = (v: number) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(v);
  const fmtCompact = (v: number): string => {
    if (v >= 1_000_000) return `$${(v / 1_000_000).toFixed(1)}M`;
    if (v >= 1_000) return `$${(v / 1_000).toFixed(0)}K`;
    return `$${v.toFixed(0)}`;
  };
  const fmtPct = (v: number) => `${(v * 100).toFixed(1)}%`;

  // Toggle accordion
  const toggleAccordion = (id: string) => {
    setExpandedAccordions(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  // Get strategy name from results
  const getStrategyName = (strategyId: string): string => {
    return results[strategyId]?.strategy.name ?? strategyId;
  };

  // PDF export — single mode
  const generateSinglePDF = (result: FireResult) => {
    const doc = new jsPDF();
    const { fire_summary, scenarios, strategy, weighted_returns } = result;
    let y = 20;

    doc.setFontSize(20);
    doc.setTextColor(26, 26, 46);
    doc.text('FIRE Investment Strategy Report', 105, y, { align: 'center' });
    y += 10;
    doc.setFontSize(10);
    doc.setTextColor(100);
    doc.text(`Generated: ${new Date().toLocaleDateString()}`, 105, y, { align: 'center' });
    y += 15;

    doc.setFontSize(14);
    doc.setTextColor(26, 115, 232);
    doc.text(`Strategy: ${strategy.name}`, 14, y);
    y += 10;

    autoTable(doc, {
      startY: y,
      head: [['Parameter', 'Value']],
      body: [
        ['FIRE Target', fmt(result.inputs.fire_target)],
        ['Current Savings', fmt(result.inputs.current_savings)],
        ['Annual Contribution', fmt(result.inputs.annual_contribution)],
        ['Contribution Frequency', result.inputs.contribution_frequency],
        ['Weighted Return (Base)', `${weighted_returns.base}%`],
        ['Weighted Return (Best)', `${weighted_returns.best}%`],
        ['Weighted Return (Worst)', `${weighted_returns.worst}%`],
      ],
      theme: 'striped',
      headStyles: { fillColor: [26, 115, 232] },
      margin: { left: 14, right: 14 },
    });
    y = (doc as any).lastAutoTable.finalY + 15;

    doc.setFontSize(14);
    doc.setTextColor(26, 115, 232);
    doc.text('FIRE Summary', 14, y);
    y += 5;

    autoTable(doc, {
      startY: y,
      head: [['Metric', 'Value']],
      body: [
        ['Base Case Years', fire_summary.base_years?.toString() ?? 'N/A'],
        ['Best Case Years', fire_summary.best_years?.toString() ?? 'N/A'],
        ['Worst Case Years', fire_summary.worst_years?.toString() ?? 'N/A'],
        ['Probability-Weighted Years', fire_summary.probability_weighted_years?.toFixed(1) ?? 'N/A'],
        ['Annual Passive Income (4% SWR)', fmt(fire_summary.annual_passive_income_at_fire)],
        ['Monthly Passive Income', fmt(fire_summary.monthly_passive_income_at_fire)],
      ],
      theme: 'striped',
      headStyles: { fillColor: [26, 115, 232] },
      margin: { left: 14, right: 14 },
    });
    y = (doc as any).lastAutoTable.finalY + 15;

    doc.setFontSize(14);
    doc.setTextColor(26, 115, 232);
    doc.text('ETF Holdings', 14, y);
    y += 5;

    autoTable(doc, {
      startY: y,
      head: [['Symbol', 'Name', 'Allocation', 'Price', 'Div Yield']],
      body: strategy.etfs.map(e => [
        e.symbol,
        e.name,
        fmtPct(e.allocation),
        e.current_price ? `$${e.current_price.toFixed(2)}` : 'N/A',
        e.dividend_yield ? `$${e.dividend_yield.toFixed(2)}` : 'N/A',
      ]),
      theme: 'striped',
      headStyles: { fillColor: [26, 115, 232] },
      margin: { left: 14, right: 14 },
    });

    doc.addPage();
    y = 20;
    doc.setFontSize(14);
    doc.setTextColor(26, 115, 232);
    doc.text('Year-by-Year Projections (Base Case)', 14, y);
    y += 8;

    const projRows = scenarios.base.yearly_projections
      .filter((p, i) => i === 0 || (p.year % 5 === 0) || i === scenarios.base.yearly_projections.length - 1);

    autoTable(doc, {
      startY: y,
      head: [['Year', 'Balance', 'Invested', 'Growth']],
      body: projRows.map(p => [
        p.year.toString(),
        fmt(p.balance),
        fmt(p.invested),
        fmt(p.growth),
      ]),
      theme: 'striped',
      headStyles: { fillColor: [26, 115, 232] },
      margin: { left: 14, right: 14 },
      styles: { fontSize: 9 },
    });

    return doc;
  };

  // PDF export — multi mode
  const generateMultiPDF = () => {
    const doc = new jsPDF();
    const strategyIds = Object.keys(results);
    let y = 20;

    // Title page
    doc.setFontSize(20);
    doc.setTextColor(26, 26, 46);
    doc.text('FIRE Strategy Comparison Report', 105, y, { align: 'center' });
    y += 10;
    doc.setFontSize(10);
    doc.setTextColor(100);
    doc.text(`Generated: ${new Date().toLocaleDateString()}`, 105, y, { align: 'center' });
    y += 5;
    doc.text(`Comparing ${strategyIds.length} strategies`, 105, y, { align: 'center' });
    y += 15;

    // Comparison metrics table
    doc.setFontSize(14);
    doc.setTextColor(26, 115, 232);
    doc.text('Strategy Comparison', 14, y);
    y += 8;

    const headers = ['Metric', ...strategyIds.map(id => getStrategyName(id))];
    const metrics = buildComparisonMetrics();
    const body = metrics.map(m => [
      m.label,
      ...strategyIds.map(id => m.values[id] ?? 'N/A'),
    ]);

    autoTable(doc, {
      startY: y,
      head: [headers],
      body,
      theme: 'striped',
      headStyles: { fillColor: [26, 115, 232] },
      margin: { left: 14, right: 14 },
      styles: { fontSize: 9 },
    });
    y = (doc as any).lastAutoTable.finalY + 10;

    // Inputs summary
    const firstResult = Object.values(results)[0];
    doc.setFontSize(12);
    doc.setTextColor(26, 115, 232);
    doc.text('Inputs', 14, y);
    y += 5;

    autoTable(doc, {
      startY: y,
      head: [['Parameter', 'Value']],
      body: [
        ['FIRE Target', fmt(firstResult.inputs.fire_target)],
        ['Current Savings', fmt(firstResult.inputs.current_savings)],
        ['Annual Contribution', fmt(firstResult.inputs.annual_contribution)],
        ['Contribution Frequency', firstResult.inputs.contribution_frequency],
      ],
      theme: 'striped',
      headStyles: { fillColor: [26, 115, 232] },
      margin: { left: 14, right: 14 },
      styles: { fontSize: 9 },
    });

    // Per-strategy detail pages
    strategyIds.forEach((strategyId) => {
      const result = results[strategyId];
      const { fire_summary, scenarios, strategy, weighted_returns } = result;

      doc.addPage();
      y = 20;

      doc.setFontSize(16);
      doc.setTextColor(26, 26, 46);
      doc.text(`Strategy: ${strategy.name}`, 14, y);
      y += 12;

      // FIRE Summary
      doc.setFontSize(12);
      doc.setTextColor(26, 115, 232);
      doc.text('FIRE Summary', 14, y);
      y += 5;

      autoTable(doc, {
        startY: y,
        head: [['Metric', 'Value']],
        body: [
          ['Base Case Years', fire_summary.base_years?.toString() ?? 'N/A'],
          ['Best Case Years', fire_summary.best_years?.toString() ?? 'N/A'],
          ['Worst Case Years', fire_summary.worst_years?.toString() ?? 'N/A'],
          ['Probability-Weighted Years', fire_summary.probability_weighted_years?.toFixed(1) ?? 'N/A'],
          ['Annual Passive Income (4% SWR)', fmt(fire_summary.annual_passive_income_at_fire)],
          ['Monthly Passive Income', fmt(fire_summary.monthly_passive_income_at_fire)],
          ['Weighted Return (Base)', `${weighted_returns.base}%`],
          ['Base Case ROI', `${scenarios.base.roi_pct.toFixed(1)}%`],
        ],
        theme: 'striped',
        headStyles: { fillColor: [26, 115, 232] },
        margin: { left: 14, right: 14 },
        styles: { fontSize: 9 },
      });
      y = (doc as any).lastAutoTable.finalY + 12;

      // ETF holdings
      doc.setFontSize(12);
      doc.setTextColor(26, 115, 232);
      doc.text('ETF Holdings', 14, y);
      y += 5;

      autoTable(doc, {
        startY: y,
        head: [['Symbol', 'Name', 'Allocation', 'Price', 'Div Yield']],
        body: strategy.etfs.map(e => [
          e.symbol,
          e.name,
          fmtPct(e.allocation),
          e.current_price ? `$${e.current_price.toFixed(2)}` : 'N/A',
          e.dividend_yield ? `$${e.dividend_yield.toFixed(2)}` : 'N/A',
        ]),
        theme: 'striped',
        headStyles: { fillColor: [26, 115, 232] },
        margin: { left: 14, right: 14 },
        styles: { fontSize: 9 },
      });
      y = (doc as any).lastAutoTable.finalY + 12;

      // Projections
      doc.setFontSize(12);
      doc.setTextColor(26, 115, 232);
      doc.text('Year-by-Year Projections (Base Case)', 14, y);
      y += 5;

      const projRows = scenarios.base.yearly_projections
        .filter((p, i) => i === 0 || (p.year % 5 === 0) || i === scenarios.base.yearly_projections.length - 1);

      autoTable(doc, {
        startY: y,
        head: [['Year', 'Balance', 'Invested', 'Growth']],
        body: projRows.map(p => [
          p.year.toString(),
          fmt(p.balance),
          fmt(p.invested),
          fmt(p.growth),
        ]),
        theme: 'striped',
        headStyles: { fillColor: [26, 115, 232] },
        margin: { left: 14, right: 14 },
        styles: { fontSize: 9 },
      });
    });

    return doc;
  };

  const generatePDF = () => {
    if (!hasResults) return;

    const doc = isMultiMode ? generateMultiPDF() : generateSinglePDF(Object.values(results)[0]);

    // Footer
    const pages = doc.getNumberOfPages();
    for (let i = 1; i <= pages; i++) {
      doc.setPage(i);
      doc.setFontSize(8);
      doc.setTextColor(150);
      doc.text(`Page ${i} of ${pages}`, 105, 290, { align: 'center' });
      doc.text('Generated by Wealth Financial Systems - FIRE Investment Strategy', 105, 295, { align: 'center' });
    }

    const prefix = isMultiMode ? 'fire-comparison' : 'fire-calculator';
    doc.save(`${prefix}-${new Date().toISOString().split('T')[0]}.pdf`);
  };

  // Build comparison metrics for multi-mode
  const buildComparisonMetrics = () => {
    const strategyIds = Object.keys(results);

    type MetricRow = {
      label: string;
      values: Record<string, string>;
      bestId: string | null;
      lowerIsBetter: boolean;
    };

    const rows: MetricRow[] = [
      {
        label: 'Base Years to FIRE',
        values: {},
        bestId: null,
        lowerIsBetter: true,
      },
      {
        label: 'Best Case Years',
        values: {},
        bestId: null,
        lowerIsBetter: true,
      },
      {
        label: 'Worst Case Years',
        values: {},
        bestId: null,
        lowerIsBetter: true,
      },
      {
        label: 'Weighted Estimate',
        values: {},
        bestId: null,
        lowerIsBetter: true,
      },
      {
        label: 'Annual Passive Income',
        values: {},
        bestId: null,
        lowerIsBetter: false,
      },
      {
        label: 'Monthly Passive Income',
        values: {},
        bestId: null,
        lowerIsBetter: false,
      },
      {
        label: 'Base ROI',
        values: {},
        bestId: null,
        lowerIsBetter: false,
      },
      {
        label: 'Weighted Return',
        values: {},
        bestId: null,
        lowerIsBetter: false,
      },
    ];

    strategyIds.forEach(id => {
      const r = results[id];
      const fs = r.fire_summary;
      rows[0].values[id] = fs.base_years != null ? `${fs.base_years}` : '60+';
      rows[1].values[id] = fs.best_years != null ? `${fs.best_years}` : '60+';
      rows[2].values[id] = fs.worst_years != null ? `${fs.worst_years}` : '60+';
      rows[3].values[id] = fs.probability_weighted_years != null ? `${fs.probability_weighted_years.toFixed(1)} yrs` : 'N/A';
      rows[4].values[id] = fmt(fs.annual_passive_income_at_fire);
      rows[5].values[id] = fmt(fs.monthly_passive_income_at_fire);
      rows[6].values[id] = `${r.scenarios.base.roi_pct.toFixed(1)}%`;
      rows[7].values[id] = `${r.weighted_returns.base}%`;
    });

    // Determine best value per row
    const numericExtractors: ((id: string) => number | null)[] = [
      (id) => results[id].fire_summary.base_years,
      (id) => results[id].fire_summary.best_years,
      (id) => results[id].fire_summary.worst_years,
      (id) => results[id].fire_summary.probability_weighted_years,
      (id) => results[id].fire_summary.annual_passive_income_at_fire,
      (id) => results[id].fire_summary.monthly_passive_income_at_fire,
      (id) => results[id].scenarios.base.roi_pct,
      (id) => results[id].weighted_returns.base,
    ];

    rows.forEach((row, rowIdx) => {
      const extractor = numericExtractors[rowIdx];
      let bestVal: number | null = null;
      let bestStrategyId: string | null = null;

      strategyIds.forEach(id => {
        const val = extractor(id);
        if (val == null) return;
        if (bestVal == null ||
          (row.lowerIsBetter && val < bestVal) ||
          (!row.lowerIsBetter && val > bestVal)) {
          bestVal = val;
          bestStrategyId = id;
        }
      });

      row.bestId = bestStrategyId;
    });

    return rows;
  };

  // ── Render: Input Tab ──
  const renderInputTab = () => (
    <div className="fire-form">
      {/* Strategy Selection */}
      <div className="form-section">
        <h3>Investment Strategy</h3>
        <p className="form-hint">
          {isCustomSelected
            ? 'Custom strategy selected. Create your own mix below.'
            : `Select up to ${MAX_STRATEGIES} strategies to compare side-by-side.`}
        </p>
        {maxLimitHint && (
          <div className="max-limit-hint">Maximum {MAX_STRATEGIES} strategies for comparison</div>
        )}

        {strategiesLoading ? (
          <div className="strategies-loading">Loading strategies...</div>
        ) : (
          <div className="strategy-cards">
            {strategies.map(s => {
              const isSelected = selectedStrategyIds.includes(s.id);
              const selectionIndex = selectedStrategyIds.indexOf(s.id);
              return (
                <div
                  key={s.id}
                  className={`strategy-card ${isSelected ? 'selected multi-selected' : ''}`}
                  onClick={() => selectStrategy(s.id)}
                  style={{ '--risk-color': RISK_COLORS[s.risk_level] || '#3b82f6' } as React.CSSProperties}
                >
                  {isSelected && (
                    <div className="strategy-card-checkbox">
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                        <circle cx="8" cy="8" r="8" fill={STRATEGY_COMPARE_COLORS[selectionIndex % STRATEGY_COMPARE_COLORS.length]} />
                        <path d="M4.5 8L7 10.5L11.5 5.5" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    </div>
                  )}
                  {isSelected && selectedStrategyIds.length > 1 && (
                    <div className="strategy-card-count">{selectionIndex + 1}/{selectedStrategyIds.length}</div>
                  )}
                  <div className="strategy-card-header">
                    <span className="strategy-card-name">{s.name}</span>
                    <span className="strategy-risk-badge" style={{ background: RISK_COLORS[s.risk_level] }}>
                      Risk {s.risk_level}
                    </span>
                  </div>
                  <p className="strategy-card-desc">{s.description}</p>
                  <div className="strategy-etf-chips">
                    {s.etfs.map(e => (
                      <span key={e.symbol} className="etf-chip">
                        {e.symbol} ({(e.allocation * 100).toFixed(0)}%)
                      </span>
                    ))}
                  </div>
                  {(s.etfs[0]?.current_price ?? 0) > 0 && (
                    <div className="strategy-prices">
                      {s.etfs.map(e => (
                        <span key={e.symbol} className="etf-price">{e.symbol}: ${e.current_price?.toFixed(2)}</span>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}

            {/* Custom card */}
            <div
              className={`strategy-card strategy-card-custom ${isCustomSelected ? 'selected multi-selected' : ''}`}
              onClick={() => selectStrategy('custom')}
              style={{ '--risk-color': RISK_COLORS[0] } as React.CSSProperties}
            >
              {isCustomSelected && (
                <div className="strategy-card-checkbox">
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <circle cx="8" cy="8" r="8" fill={RISK_COLORS[0]} />
                    <path d="M4.5 8L7 10.5L11.5 5.5" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </div>
              )}
              <div className="strategy-card-header">
                <span className="strategy-card-name">Custom Strategy</span>
                <span className="strategy-risk-badge" style={{ background: RISK_COLORS[0] }}>Custom</span>
              </div>
              <p className="strategy-card-desc">Enter any ETF tickers with your own allocations and return assumptions.</p>
            </div>
          </div>
        )}
      </div>

      {/* Custom ETF inputs */}
      {isCustomSelected && (
        <div className="form-section">
          <h3>Custom ETFs</h3>
          <p className="form-hint">Enter 1-3 ETF tickers. Allocations must total 100%.</p>
          <div className="custom-etfs-grid">
            {customETFs.map((etf, idx) => (
              <div key={idx} className="custom-etf-row">
                <div className="form-group">
                  <label>Ticker {idx + 1}</label>
                  <input
                    type="text"
                    value={etf.symbol}
                    onChange={e => handleCustomETFChange(idx, 'symbol', e.target.value.toUpperCase())}
                    placeholder="e.g. VOO"
                    maxLength={6}
                  />
                </div>
                <div className="form-group">
                  <label>Allocation (%)</label>
                  <input
                    type="number"
                    value={Math.round(etf.allocation * 100)}
                    onChange={e => handleCustomETFChange(idx, 'allocation', (parseFloat(e.target.value) || 0) / 100)}
                    min={0} max={100} step={1}
                  />
                </div>
                <div className="form-group">
                  <label>Base Return (%)</label>
                  <input
                    type="number"
                    value={(etf.base_return * 100).toFixed(1)}
                    onChange={e => handleCustomETFChange(idx, 'base_return', (parseFloat(e.target.value) || 0) / 100)}
                    step={0.5}
                  />
                </div>
                <div className="form-group">
                  <label>Best Return (%)</label>
                  <input
                    type="number"
                    value={(etf.best_return * 100).toFixed(1)}
                    onChange={e => handleCustomETFChange(idx, 'best_return', (parseFloat(e.target.value) || 0) / 100)}
                    step={0.5}
                  />
                </div>
                <div className="form-group">
                  <label>Worst Return (%)</label>
                  <input
                    type="number"
                    value={(etf.worst_return * 100).toFixed(1)}
                    onChange={e => handleCustomETFChange(idx, 'worst_return', (parseFloat(e.target.value) || 0) / 100)}
                    step={0.5}
                  />
                </div>
              </div>
            ))}
          </div>
          <div className="allocation-total">
            Total: {(customETFs.reduce((s, e) => s + e.allocation, 0) * 100).toFixed(0)}%
          </div>
        </div>
      )}

      {/* Allocation overrides for preset strategies — only when exactly 1 preset selected */}
      {!isCustomSelected && selectedStrategyIds.length === 1 && selectedStrategy && (
        <div className="form-section">
          <h3>Allocation (adjust optional)</h3>
          <p className="form-hint">Default allocations for this strategy. Drag to customize.</p>
          <div className="form-grid">
            {selectedStrategy.etfs.map((etf, idx) => {
              const alloc = customAllocations ? customAllocations[idx] : etf.allocation;
              return (
                <div key={etf.symbol} className="form-group">
                  <label>{etf.symbol} — {etf.role} ({(alloc * 100).toFixed(0)}%)</label>
                  <input
                    type="range"
                    value={alloc * 100}
                    onChange={e => handleAllocationOverride(idx, parseFloat(e.target.value) / 100)}
                    min={0} max={100} step={5}
                  />
                </div>
              );
            })}
          </div>
          <div className="allocation-total">
            Total: {((customAllocations || selectedStrategy.etfs.map(e => e.allocation)).reduce((s, v) => s + v, 0) * 100).toFixed(0)}%
          </div>
        </div>
      )}

      {/* FIRE Inputs */}
      <div className="form-section">
        <h3>FIRE Inputs</h3>
        <div className="form-grid">
          <div className="form-group">
            <label>FIRE Target ($)</label>
            <input
              type="number"
              value={fireTarget}
              onChange={e => setFireTarget(parseFloat(e.target.value) || 0)}
              min={10000} max={100000000} step={50000}
            />
          </div>
          <div className="form-group">
            <label>Current Savings ($)</label>
            <input
              type="number"
              value={currentSavings}
              onChange={e => setCurrentSavings(parseFloat(e.target.value) || 0)}
              min={0} step={5000}
            />
          </div>
          <div className="form-group">
            <label>Annual Contribution ($)</label>
            <input
              type="number"
              value={annualContribution}
              onChange={e => setAnnualContribution(parseFloat(e.target.value) || 0)}
              min={0} step={5000}
            />
          </div>
          <div className="form-group">
            <label>Contribution Frequency</label>
            <select value={frequency} onChange={e => setFrequency(e.target.value as any)}>
              <option value="monthly">Monthly</option>
              <option value="quarterly">Quarterly</option>
              <option value="annual">Annual</option>
            </select>
          </div>
        </div>
      </div>

      {/* Run Button */}
      <div className="form-actions">
        <button
          className={`btn-primary btn-run ${selectedStrategyIds.length > 1 ? 'multi' : ''}`}
          onClick={runCalculation}
          disabled={loading || selectedStrategyIds.length === 0}
        >
          {loading
            ? (loadingProgress || 'Calculating...')
            : selectedStrategyIds.length > 1
              ? `Compare ${selectedStrategyIds.length} Strategies`
              : 'Calculate FIRE Projection'}
        </button>
      </div>
    </div>
  );

  // ── Render: Comparison Chart (multi-mode) ──
  const renderComparisonChart = () => {
    const strategyIds = Object.keys(results);

    // Merge all strategies' base-case projections by year index
    let maxLen = 0;
    strategyIds.forEach(id => {
      const len = results[id].scenarios.base.yearly_projections.length;
      if (len > maxLen) maxLen = len;
    });

    const chartData: Record<string, any>[] = [];
    for (let i = 0; i < maxLen; i++) {
      const point: Record<string, any> = { year: i + 1 };
      strategyIds.forEach(id => {
        const proj = results[id].scenarios.base.yearly_projections[i];
        point[id] = proj?.balance ?? null;
      });
      chartData.push(point);
    }

    return (
      <div className="fire-chart-container fire-compare-chart">
        <h4>Strategy Comparison — Base Case Growth</h4>
        <ResponsiveContainer width="100%" height={360}>
          <LineChart data={chartData} margin={{ top: 10, right: 30, left: 10, bottom: 0 }}>
            <XAxis dataKey="year" stroke="#64748b" tick={{ fill: '#94a3b8', fontSize: 12 }} label={{ value: 'Year', position: 'insideBottomRight', offset: -5, fill: '#94a3b8' }} />
            <YAxis stroke="#64748b" tick={{ fill: '#94a3b8', fontSize: 12 }} tickFormatter={fmtCompact} />
            <Tooltip
              contentStyle={{ background: '#1e293b', border: '1px solid rgba(255,255,255,0.15)', borderRadius: 8, color: '#e2e8f0' }}
              labelFormatter={y => `Year ${y}`}
              formatter={(v: any, name: any) => [fmt(v ?? 0), getStrategyName(name)]}
            />
            <Legend formatter={(value: string) => getStrategyName(value)} />
            <ReferenceLine y={fireTarget} stroke="#f59e0b" strokeDasharray="6 3" label={{ value: `FIRE Target ${fmtCompact(fireTarget)}`, fill: '#f59e0b', fontSize: 12, position: 'right' }} />
            {strategyIds.map((id, idx) => (
              <Line
                key={id}
                type="monotone"
                dataKey={id}
                stroke={STRATEGY_COMPARE_COLORS[idx % STRATEGY_COMPARE_COLORS.length]}
                strokeWidth={2.5}
                dot={false}
                name={id}
              />
            ))}
          </LineChart>
        </ResponsiveContainer>
      </div>
    );
  };

  // ── Render: Comparison Metrics Table (multi-mode) ──
  const renderComparisonTable = () => {
    const strategyIds = Object.keys(results);
    const metrics = buildComparisonMetrics();

    return (
      <div className="fire-compare-table-wrapper">
        <h3>Strategy Comparison</h3>
        <div className="projections-table-container">
          <table className="projections-table fire-compare-table">
            <thead>
              <tr>
                <th>Metric</th>
                {strategyIds.map((id, idx) => (
                  <th key={id}>
                    <span className="compare-th-dot" style={{ background: STRATEGY_COMPARE_COLORS[idx % STRATEGY_COMPARE_COLORS.length] }} />
                    {getStrategyName(id)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {metrics.map((row, rowIdx) => (
                <tr key={rowIdx}>
                  <td>{row.label}</td>
                  {strategyIds.map(id => (
                    <td key={id} className={row.bestId === id ? 'best-value' : ''}>
                      {row.values[id] ?? 'N/A'}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  // ── Render: Strategy Details Accordion (multi-mode) ──
  const renderStrategyAccordion = () => {
    const strategyIds = Object.keys(results);

    return (
      <div className="fire-accordions">
        <h3>Strategy Details</h3>
        {strategyIds.map((id, idx) => {
          const result = results[id];
          const isExpanded = expandedAccordions.has(id);
          return (
            <div key={id} className="fire-accordion">
              <button
                className="fire-accordion-header"
                onClick={() => toggleAccordion(id)}
              >
                <span className="accordion-color-dot" style={{ background: STRATEGY_COMPARE_COLORS[idx % STRATEGY_COMPARE_COLORS.length] }} />
                <span className="accordion-title">{result.strategy.name}</span>
                <span className="accordion-summary">
                  {result.fire_summary.base_years != null
                    ? `${result.fire_summary.base_years} yrs base`
                    : '60+ yrs'}
                  {' | '}
                  {result.weighted_returns.base}% return
                </span>
                <span className={`accordion-chevron ${isExpanded ? 'expanded' : ''}`}>
                  <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                    <path d="M3 4.5L6 7.5L9 4.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </span>
              </button>
              {isExpanded && (
                <div className="fire-accordion-body">
                  <div className="etf-fundamentals">
                    {result.strategy.etfs.map(etf => (
                      <div key={etf.symbol} className="etf-fund-card">
                        <div className="etf-fund-top">
                          <div className="etf-fund-identity">
                            <span className="etf-symbol">{etf.symbol}</span>
                            <span className="etf-alloc">{(etf.allocation * 100).toFixed(0)}%</span>
                          </div>
                          <div className="etf-detail-name">{etf.name}</div>
                          <div className="etf-detail-role">{etf.role}</div>
                        </div>
                        <div className="etf-fund-stats">
                          {etf.current_price != null && etf.current_price > 0 && (
                            <div className="etf-stat">
                              <span className="etf-stat-label">Price</span>
                              <span className="etf-stat-value">${etf.current_price.toFixed(2)}</span>
                            </div>
                          )}
                          {etf.expense_ratio != null && (
                            <div className="etf-stat">
                              <span className="etf-stat-label">Expense Ratio</span>
                              <span className="etf-stat-value">{(etf.expense_ratio * 100).toFixed(2)}%</span>
                            </div>
                          )}
                          {etf.dividend_yield != null && etf.dividend_yield > 0 && (
                            <div className="etf-stat">
                              <span className="etf-stat-label">Dividend</span>
                              <span className="etf-stat-value">${etf.dividend_yield.toFixed(2)}</span>
                            </div>
                          )}
                          {etf.beta != null && (
                            <div className="etf-stat">
                              <span className="etf-stat-label">Beta</span>
                              <span className="etf-stat-value">{etf.beta.toFixed(2)}</span>
                            </div>
                          )}
                          {etf.market_cap != null && etf.market_cap > 0 && (
                            <div className="etf-stat">
                              <span className="etf-stat-label">AUM</span>
                              <span className="etf-stat-value">{fmtCompact(etf.market_cap)}</span>
                            </div>
                          )}
                        </div>
                        {etf.returns && (
                          <div className="etf-returns-table">
                            <div className="etf-returns-header">Historical Returns</div>
                            <div className="etf-returns-grid">
                              {(['1m', '3m', '6m', 'ytd', '1y', '3y', '5y', '10y'] as const).map(period => {
                                const val = etf.returns?.[period];
                                if (val == null) return null;
                                const isPositive = val >= 0;
                                return (
                                  <div key={period} className="etf-return-cell">
                                    <span className="etf-return-period">{period.toUpperCase()}</span>
                                    <span className={`etf-return-value ${isPositive ? 'positive' : 'negative'}`}>
                                      {isPositive ? '+' : ''}{val.toFixed(1)}%
                                    </span>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  // ── Render: Single-mode Results (existing behavior) ──
  const renderSingleResults = (result: FireResult) => {
    const { fire_summary, scenarios, strategy, weighted_returns } = result;

    const maxLen = Math.max(
      scenarios.base.yearly_projections.length,
      scenarios.best.yearly_projections.length,
      scenarios.worst.yearly_projections.length,
    );
    const chartData = [];
    for (let i = 0; i < maxLen; i++) {
      chartData.push({
        year: i + 1,
        base: scenarios.base.yearly_projections[i]?.balance ?? null,
        best: scenarios.best.yearly_projections[i]?.balance ?? null,
        worst: scenarios.worst.yearly_projections[i]?.balance ?? null,
      });
    }

    return (
      <div className="fire-results">
        {/* Chart */}
        <div className="fire-chart-container">
          <h4>Portfolio Growth — 3 Scenarios</h4>
          <ResponsiveContainer width="100%" height={360}>
            <LineChart data={chartData} margin={{ top: 10, right: 30, left: 10, bottom: 0 }}>
              <XAxis dataKey="year" stroke="#64748b" tick={{ fill: '#94a3b8', fontSize: 12 }} label={{ value: 'Year', position: 'insideBottomRight', offset: -5, fill: '#94a3b8' }} />
              <YAxis stroke="#64748b" tick={{ fill: '#94a3b8', fontSize: 12 }} tickFormatter={fmtCompact} />
              <Tooltip
                contentStyle={{ background: '#1e293b', border: '1px solid rgba(255,255,255,0.15)', borderRadius: 8, color: '#e2e8f0' }}
                labelFormatter={y => `Year ${y}`}
                formatter={(v: any, name: any) => [fmt(v ?? 0), (name || '').charAt(0).toUpperCase() + (name || '').slice(1) + ' Case']}
              />
              <Legend />
              <ReferenceLine y={fireTarget} stroke="#f59e0b" strokeDasharray="6 3" label={{ value: `FIRE Target ${fmtCompact(fireTarget)}`, fill: '#f59e0b', fontSize: 12, position: 'right' }} />
              <Line type="monotone" dataKey="best" stroke="#22c55e" strokeWidth={2} dot={false} name="Best" />
              <Line type="monotone" dataKey="base" stroke="#3b82f6" strokeWidth={2.5} dot={false} name="Base" />
              <Line type="monotone" dataKey="worst" stroke="#ef4444" strokeWidth={2} dot={false} name="Worst" />
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* FIRE Summary */}
        <div className="results-summary">
          <div className="summary-header">
            <h3>FIRE Summary</h3>
            <button className="btn-download" onClick={generatePDF}>Download PDF Report</button>
          </div>
          <p>
            Using the <strong>{strategy.name}</strong> strategy with {fmt(result.inputs.annual_contribution)}/year contributions:
          </p>
          {fire_summary.base_years != null ? (
            <p>
              You'll reach your <strong>{fmt(fireTarget)}</strong> FIRE target in an estimated{' '}
              <strong>{fire_summary.base_years} years</strong> (base case).
              Best case: <strong>{fire_summary.best_years} years</strong>.
              Worst case: <strong>{fire_summary.worst_years != null ? `${fire_summary.worst_years} years` : '60+ years'}</strong>.
            </p>
          ) : (
            <p>
              With current inputs, the base case does not reach the FIRE target within 60 years.
              Consider increasing contributions or lowering your target.
            </p>
          )}
          {fire_summary.probability_weighted_years != null && (
            <p>
              Probability-weighted estimate: <strong>{fire_summary.probability_weighted_years.toFixed(1)} years</strong>.
              At 4% SWR, your passive income would be <strong>{fmt(fire_summary.annual_passive_income_at_fire)}/year</strong>{' '}
              ({fmt(fire_summary.monthly_passive_income_at_fire)}/month).
            </p>
          )}
        </div>

        {/* KPI Cards */}
        <h3>Key Metrics</h3>
        <div className="kpi-grid">
          <div className="kpi-card">
            <div className="kpi-label">Base Case Years</div>
            <div className="kpi-value">{fire_summary.base_years ?? '60+'}</div>
          </div>
          <div className="kpi-card">
            <div className="kpi-label">Best Case Years</div>
            <div className="kpi-value fire-best">{fire_summary.best_years ?? '60+'}</div>
          </div>
          <div className="kpi-card">
            <div className="kpi-label">Worst Case Years</div>
            <div className="kpi-value fire-worst">{fire_summary.worst_years ?? '60+'}</div>
          </div>
          <div className="kpi-card">
            <div className="kpi-label">Weighted Estimate</div>
            <div className="kpi-value">{fire_summary.probability_weighted_years?.toFixed(1) ?? 'N/A'} yrs</div>
          </div>
          <div className="kpi-card">
            <div className="kpi-label">Annual Passive Income</div>
            <div className="kpi-value">{fmt(fire_summary.annual_passive_income_at_fire)}</div>
          </div>
          <div className="kpi-card">
            <div className="kpi-label">Monthly Passive Income</div>
            <div className="kpi-value">{fmt(fire_summary.monthly_passive_income_at_fire)}</div>
          </div>
          <div className="kpi-card">
            <div className="kpi-label">Base Case ROI</div>
            <div className="kpi-value">{scenarios.base.roi_pct.toFixed(1)}%</div>
          </div>
          <div className="kpi-card">
            <div className="kpi-label">Weighted Return</div>
            <div className="kpi-value">{weighted_returns.base}%</div>
          </div>
        </div>

        {/* ETF Fundamentals */}
        <h3>ETF Fundamentals</h3>
        <div className="etf-fundamentals">
          {strategy.etfs.map(etf => (
            <div key={etf.symbol} className="etf-fund-card">
              <div className="etf-fund-top">
                <div className="etf-fund-identity">
                  <span className="etf-symbol">{etf.symbol}</span>
                  <span className="etf-alloc">{(etf.allocation * 100).toFixed(0)}%</span>
                </div>
                <div className="etf-detail-name">{etf.name}</div>
                <div className="etf-detail-role">{etf.role}</div>
              </div>

              <div className="etf-fund-stats">
                {etf.current_price != null && etf.current_price > 0 && (
                  <div className="etf-stat">
                    <span className="etf-stat-label">Price</span>
                    <span className="etf-stat-value">${etf.current_price.toFixed(2)}</span>
                  </div>
                )}
                {etf.expense_ratio != null && (
                  <div className="etf-stat">
                    <span className="etf-stat-label">Expense Ratio</span>
                    <span className="etf-stat-value">{(etf.expense_ratio * 100).toFixed(2)}%</span>
                  </div>
                )}
                {etf.dividend_yield != null && etf.dividend_yield > 0 && (
                  <div className="etf-stat">
                    <span className="etf-stat-label">Dividend</span>
                    <span className="etf-stat-value">${etf.dividend_yield.toFixed(2)}</span>
                  </div>
                )}
                {etf.beta != null && (
                  <div className="etf-stat">
                    <span className="etf-stat-label">Beta</span>
                    <span className="etf-stat-value">{etf.beta.toFixed(2)}</span>
                  </div>
                )}
                {etf.market_cap != null && etf.market_cap > 0 && (
                  <div className="etf-stat">
                    <span className="etf-stat-label">AUM</span>
                    <span className="etf-stat-value">{fmtCompact(etf.market_cap)}</span>
                  </div>
                )}
              </div>

              {etf.returns && (
                <div className="etf-returns-table">
                  <div className="etf-returns-header">Historical Returns</div>
                  <div className="etf-returns-grid">
                    {(['1m', '3m', '6m', 'ytd', '1y', '3y', '5y', '10y'] as const).map(period => {
                      const val = etf.returns?.[period];
                      if (val == null) return null;
                      const isPositive = val >= 0;
                      return (
                        <div key={period} className="etf-return-cell">
                          <span className="etf-return-period">{period.toUpperCase()}</span>
                          <span className={`etf-return-value ${isPositive ? 'positive' : 'negative'}`}>
                            {isPositive ? '+' : ''}{val.toFixed(1)}%
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    );
  };

  // ── Render: Results Tab ──
  const renderResultsTab = () => {
    if (!hasResults) return <p>No results yet. Run a calculation first.</p>;

    if (isMultiMode) {
      return (
        <div className="fire-results">
          {renderComparisonChart()}

          <div className="summary-header">
            <h3 style={{ margin: 0 }}>Comparison Results</h3>
            <button className="btn-download" onClick={generatePDF}>Download PDF Report</button>
          </div>

          {renderComparisonTable()}
          {renderStrategyAccordion()}
        </div>
      );
    }

    return renderSingleResults(singleResult!);
  };

  // ── Render: Projections Tab ──
  const renderProjectionsTab = () => {
    if (!hasResults) return <p>No results yet. Run a calculation first.</p>;

    // Multi-mode: sub-tabs
    if (isMultiMode) {
      const strategyIds = Object.keys(results);
      const activeSubTab = projSubTab && results[projSubTab] ? projSubTab : strategyIds[0];
      const activeResult = results[activeSubTab];
      const projections = activeResult.scenarios.base.yearly_projections;

      // Always include the year FIRE was reached so the * marker is visible
      // — without this, base_years values like 23 (not divisible by 5) get
      // filtered out and the table looks like the target was never hit.
      const fireYear = activeResult.fire_summary.base_years;
      const milestones = projections.filter(
        (p, idx) => idx === 0 || p.year % 5 === 0 || idx === projections.length - 1 || p.year === fireYear
      );

      return (
        <div className="fire-projections">
          <div className="fire-proj-subtabs">
            {strategyIds.map((id, idx) => (
              <button
                key={id}
                className={`fire-proj-subtab ${activeSubTab === id ? 'active' : ''}`}
                onClick={() => setProjSubTab(id)}
                style={activeSubTab === id ? { borderColor: STRATEGY_COMPARE_COLORS[idx % STRATEGY_COMPARE_COLORS.length] } : {}}
              >
                <span className="subtab-dot" style={{ background: STRATEGY_COMPARE_COLORS[idx % STRATEGY_COMPARE_COLORS.length] }} />
                {getStrategyName(id)}
              </button>
            ))}
          </div>

          <h3>Year-by-Year Projections (Base Case) — {getStrategyName(activeSubTab)}</h3>
          <p className="projections-hint">
            Showing milestone years. Growth is for that specific year, not cumulative.
          </p>
          <div className="projections-table-container">
            <table className="projections-table">
              <thead>
                <tr>
                  <th>Year</th>
                  <th>Balance</th>
                  <th>Total Invested</th>
                  <th>Year Growth</th>
                  <th>Contribution</th>
                </tr>
              </thead>
              <tbody>
                {milestones.map(p => {
                  const atFire = activeResult.fire_summary.base_years != null && p.year === activeResult.fire_summary.base_years;
                  return (
                    <tr key={p.year} className={atFire ? 'fire-milestone-row' : ''}>
                      <td>{p.year}{atFire && ' *'}</td>
                      <td>{fmt(p.balance)}</td>
                      <td>{fmt(p.invested)}</td>
                      <td>{fmt(p.growth)}</td>
                      <td>{fmt(p.annual_contribution)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {activeResult.fire_summary.base_years != null && (
            <p className="fire-milestone-note">* FIRE target reached</p>
          )}
        </div>
      );
    }

    // Single-mode
    const result = singleResult!;
    const { scenarios } = result;
    const projections = scenarios.base.yearly_projections;

    // Always include the year FIRE was reached so the * marker is visible
    const fireYear = result.fire_summary.base_years;
    const milestones = projections.filter(
      (p, idx) => idx === 0 || p.year % 5 === 0 || idx === projections.length - 1 || p.year === fireYear
    );

    return (
      <div className="fire-projections">
        <h3>Year-by-Year Projections (Base Case)</h3>
        <p className="projections-hint">
          Showing milestone years. Growth is for that specific year, not cumulative.
        </p>
        <div className="projections-table-container">
          <table className="projections-table">
            <thead>
              <tr>
                <th>Year</th>
                <th>Balance</th>
                <th>Total Invested</th>
                <th>Year Growth</th>
                <th>Contribution</th>
              </tr>
            </thead>
            <tbody>
              {milestones.map(p => {
                const atFire = result.fire_summary.base_years != null && p.year === result.fire_summary.base_years;
                return (
                  <tr key={p.year} className={atFire ? 'fire-milestone-row' : ''}>
                    <td>{p.year}{atFire && ' *'}</td>
                    <td>{fmt(p.balance)}</td>
                    <td>{fmt(p.invested)}</td>
                    <td>{fmt(p.growth)}</td>
                    <td>{fmt(p.annual_contribution)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {result.fire_summary.base_years != null && (
          <p className="fire-milestone-note">* FIRE target reached</p>
        )}
      </div>
    );
  };

  // ── Render: Guide Modal ──
  const renderGuideModal = () => {
    if (!showGuide) return null;

    return (
      <div className="guide-overlay" onClick={() => setShowGuide(false)}>
        <div className="guide-modal" onClick={e => e.stopPropagation()}>
          <button className="guide-close" onClick={() => setShowGuide(false)}>&times;</button>

          <div className="guide-section">
            <h3>What is the FIRE Investment Strategy?</h3>
            <p>
              FIRE (Financial Independence, Retire Early) is about building enough wealth that
              investment returns cover your living expenses — so work becomes optional. This calculator
              models how long it takes to reach your target portfolio value using real ETF strategies.
            </p>
          </div>

          <div className="guide-section">
            <h3>How It Works</h3>
            <div className="guide-steps">
              <div className="guide-step">
                <strong>Choose a Strategy</strong> — Pick from 5 preset portfolios (Conservative through
                Tech Aggressive), each built with 3 low-cost ETFs. Or create a Custom strategy with any tickers.
                You can select up to 4 strategies to compare side-by-side.
              </div>
              <div className="guide-step">
                <strong>Set Your Inputs</strong> — Enter your FIRE target (how much you need), current savings,
                annual contribution, and how often you contribute.
              </div>
              <div className="guide-step">
                <strong>3-Scenario Projections</strong> — The calculator runs base, best, and worst-case
                scenarios using each strategy's return assumptions, showing you how market conditions affect your timeline.
              </div>
              <div className="guide-step">
                <strong>FIRE Summary</strong> — See your probability-weighted years to FIRE, the annual passive
                income at your target (4% safe withdrawal rate), and a detailed year-by-year projection.
              </div>
              <div className="guide-step">
                <strong>Live ETF Data</strong> — Current prices and dividend yields are fetched in real-time
                from Financial Modeling Prep (FMP) so you always see up-to-date holdings.
              </div>
            </div>
          </div>

          <div className="guide-section">
            <h3>The 5 Strategy Presets</h3>
            <div className="guide-steps">
              <div className="guide-step">
                <strong>Conservative</strong> — BND + SCHD + VNQ. Heavy bonds, ~5.5% base return. For capital preservation.
              </div>
              <div className="guide-step">
                <strong>Balanced</strong> — VOO + VXUS + BND. Classic 70/30 split, ~7.0% base return. The default choice.
              </div>
              <div className="guide-step">
                <strong>Growth</strong> — VTI + VXUS + AVUV. Full equity with small-cap tilt, ~9.0% base return.
              </div>
              <div className="guide-step">
                <strong>Tech Aggressive</strong> — QQQ + SMH + ARKK. Concentrated tech, ~11.0% base return. High volatility.
              </div>
              <div className="guide-step">
                <strong>Dividend Income</strong> — SCHD + VYM + JEPI. Income-focused, ~7.5% base return. Steady cash flow.
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // ── Main Render ──
  return (
    <div className="fire-container">
      <Navigation />

      <main className="fire-main">
        <div className="fire-header">
          <h2>FIRE Investment Strategy</h2>
          {isPremium && (
            <p>
              Model your path to Financial Independence with real ETF strategies and 3-scenario projections.
              {' '}<button className="guide-link" onClick={() => setShowGuide(true)}>How It Works</button>
            </p>
          )}
        </div>

        <div className="tool-purpose-banner">
          <span className="tpb-icon">🎯</span>
          <span>
            <strong>Use this tool if</strong> you have a retirement savings target and want to see which investment strategy gets you there fastest — with best, base, and worst-case projections.{' '}
            Need to model taxes, Social Security, or year-by-year retirement withdrawals?{' '}
            Use <strong>Lifecycle Planner</strong> instead.
          </span>
        </div>

        {!isPremium ? (
          <div className="bv-summary-upgrade" style={{ marginTop: '1.5rem' }}>
            <div className="chat-upgrade-icon">🔒</div>
            <strong>Premier Feature</strong>
            <p>FIRE Investment Strategy is available for Premier members. Upgrade to model your path to financial independence with real ETF strategies and 3-scenario projections.</p>
            <Link to="/upgrade" className="lp-btn lp-btn-primary" style={{ marginTop: '0.75rem', display: 'inline-block', textDecoration: 'none' }}>
              Upgrade to Premier
            </Link>
          </div>
        ) : (
          <>
            {renderGuideModal()}

            {error && (
              <div className="error-message">
                {error}
                <button onClick={() => setError(null)}>Dismiss</button>
              </div>
            )}

            <div className="fire-tabs">
              <button className={`tab-btn ${activeTab === 'input' ? 'active' : ''}`} onClick={() => setActiveTab('input')}>
                Input
              </button>
              <button className={`tab-btn ${activeTab === 'results' ? 'active' : ''}`} onClick={() => setActiveTab('results')} disabled={!hasResults}>
                Results
              </button>
              <button className={`tab-btn ${activeTab === 'projections' ? 'active' : ''}`} onClick={() => setActiveTab('projections')} disabled={!hasResults}>
                Projections
              </button>
            </div>

            <div className="fire-content">
              {activeTab === 'input' && renderInputTab()}
              {activeTab === 'results' && renderResultsTab()}
              {activeTab === 'projections' && renderProjectionsTab()}
            </div>
          </>
        )}
      </main>
    </div>
  );
};
